import { Box, Link, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink } from 'react-router-dom';

import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { cohortHref } from '../listKeys';
import type { PeCohortDetail } from '../types';
import { EM_DASH, fmtLevel, fmtSigned, shortYear } from '../utils/drilldownFormat';
import { priorSchoolYear } from '../utils/schoolYear';
import { CountLink } from './CountLink';
import { SmallGroupMarker } from './SmallGroupMarker';

const KV_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1,
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 500,
  color: COLORS.muted,
  borderTop: `1px solid ${COLORS.divider}`,
  pt: 1,
} as const;
const KV_VALUE_SX = { color: COLORS.ink, fontWeight: 500 } as const;

export function CohortCard({
  cohort,
  threshold,
  schoolYear,
  lastYearKnown = true,
}: {
  cohort: PeCohortDetail;
  threshold: number;
  schoolYear: string;
  lastYearKnown?: boolean;
}) {
  const change =
    cohort.last_count != null
      ? ` · ${fmtSigned(cohort.count - cohort.last_count)} vs ${shortYear(priorSchoolYear(schoolYear))}`
      : '';
  const awaiting = lastYearKnown && cohort.last_count == null ? (cohort.awaiting_confirmation ?? 0) : 0;
  const ownValue = cohort.row_count != null ? String(cohort.row_count) : fmtLevel(cohort.row_level);
  const most =
    cohort.most && cohort.count > 0
      ? `Grades ${cohort.most.from_label}–${cohort.most.to_label} · ${cohort.most.count}`
      : EM_DASH;
  return (
    <Box
      data-testid="cohort-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2.5,
        border: `1px solid ${COLORS.divider}`,
        borderRadius: '12px',
        bgcolor: '#FFFFFF',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 1,
        }}
      >
        <Typography
          component="h4"
          sx={{
            m: 0,
            fontSize: 16,
            lineHeight: '24px',
            fontWeight: 700,
            color: COLORS.ink,
          }}
        >
          {cohort.label}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            lineHeight: '16px',
            fontWeight: 500,
            color: COLORS.muted,
            textAlign: 'right',
          }}
        >
          {cohort.definition}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 0.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          component="span"
          data-testid="cohort-card-count"
          sx={{
            fontSize: 32,
            lineHeight: '40px',
            fontWeight: 500,
            color: COLORS.link,
          }}
        >
          <CountLink count={cohort.count} listKey={cohort.cohort} />
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: 16,
            lineHeight: '24px',
            fontWeight: 500,
            color: COLORS.muted,
          }}
        >
          {`${Math.round(cohort.share * 100)}% of active ELs${change}`}
          {cohort.small && <SmallGroupMarker threshold={threshold} />}
        </Typography>
      </Box>
      {awaiting ? (
        <Typography component="p" sx={{ m: 0, fontSize: 12, lineHeight: '16px', fontWeight: 500, color: COLORS.muted }}>
          {`Last year’s count is withheld while ${awaiting} students await newcomer confirmation`}
        </Typography>
      ) : null}
      <Box sx={KV_SX}>
        <span>{cohort.row_label}</span>
        <Box component="b" sx={KV_VALUE_SX}>
          {ownValue}
        </Box>
      </Box>
      <Box sx={KV_SX}>
        <span>Met exit criteria</span>
        <Box component="b" sx={KV_VALUE_SX}>
          <CountLink count={cohort.met} listKey={`cohort:${cohort.cohort}:met`} />
        </Box>
      </Box>
      <Box sx={KV_SX}>
        <span>Most in</span>
        <Box component="b" sx={KV_VALUE_SX}>
          {most}
        </Box>
      </Box>
      <Box sx={{ ...KV_SX, justifyContent: 'flex-end' }}>
        <Link
          component={RouterLink}
          to={cohortHref(cohort.cohort)}
          state={APP_LINK_STATE}
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: COLORS.link,
            fontWeight: 600,
          }}
        >
          Detail
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </Link>
      </Box>
    </Box>
  );
}
